import { useStore } from "@nanostores/react";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isAztecWalletConnected } from "../aztec-utils";
import {
  aztecWalletAddress,
  aztecWalletError,
  aztecWalletStatus,
  connectAztecWallet as connectWallet,
  disconnectAztecWallet as disconnectWallet,
  initializeAztecWalletState,
  isAztecConnected,
} from "../state/aztecWallet";
import { l2AccountAddressQuery } from "../queries/aztec";

export function useAztecWallet() {
  const status = useStore(aztecWalletStatus);
  const address = useStore(aztecWalletAddress);
  const error = useStore(aztecWalletError);
  const isConnected = useStore(isAztecConnected);
  const queryClient = useQueryClient();
  const previousAddressRef = useRef(address);

  // Debug logging for state changes
  useEffect(() => {
    const walletInstanceConnected = isAztecWalletConnected();
    console.debug("🔧 useAztecWallet state change:", {
      status,
      address,
      error,
      isConnected,
      isConnecting: status === "connecting",
      walletInstanceConnected,
      stateMismatch: isConnected !== walletInstanceConnected,
    });
  }, [status, address, error, isConnected]);

  const connect = useCallback(async () => {
    try {
      console.debug("🚀 Starting wallet connection...");
      const address = await connectWallet();
      console.debug("✅ Wallet connection successful, address:", address);
      return address;
    } catch (error) {
      console.error("❌ Failed to connect Aztec wallet:", error);
      throw error;
    }
  }, []);

  const disconnect = useCallback(() => {
    console.debug("🔌 Disconnecting wallet...");
    disconnectWallet();
  }, []);

  useEffect(() => {
    const previousAddress = previousAddressRef.current;

    if (address) {
      queryClient.setQueryData(l2AccountAddressQuery.queryKey, address);
      queryClient.invalidateQueries({ queryKey: ["l2_account"] });
    } else if (previousAddress) {
      queryClient.removeQueries({ queryKey: ["l2_account"] });
    }

    previousAddressRef.current = address;
  }, [address, queryClient]);

  return {
    status,
    address,
    error,
    isConnected,
    isConnecting: status === "connecting",
    connect,
    disconnect,
  };
}

export function useAztecWalletInit() {
  useEffect(() => {
    initializeAztecWalletState();
  }, []);
}
